import '../../Styles/StylesDropdowns.css';

import React from 'react';
import { Form, Checkbox } from 'antd';

import { Fields } from '../fields';
import { useAuth } from 'src/AuthContext';
import {
  DOOR_STYLES,
  PRODUCT_LINES,
  CLOSET_COLORS,
  HANDLE_STYLES,
  DRAWER_STYLES,
  HARDWARE_COLORS,
  DRAWER_DOOR_COLORS,
} from '../../constants/projectConstants';

import type { DoorStyle, ClosetColor, DrawerStyle } from '../../types/project';

interface StyleDropdownsProps {
  form?: any;
}

type DropdownConfig = {
  name: string;
  label: string;
  options: { value: string; label: string; subtext?: string }[];
  required: boolean;
  placeholder: string;
  isDisabled?: (value: any) => boolean;
};

const StyleDropdowns: React.FC<StyleDropdownsProps> = ({ form: externalForm }) => {
  const internalForm = Form.useFormInstance();
  const form = externalForm || internalForm;
  const { user } = useAuth();

  // Check if user has access to door and drawer color fields
  const showColorFields = user.isMultifamily;

  // Watch form values for interdependent rules
  const mainColor = Form.useWatch('color', form);
  const drawerColor = Form.useWatch('drawerColor', form);
  const drawerStyle = Form.useWatch('drawerStyle', form);
  const doorColor = Form.useWatch('doorColor', form);
  const doorStyle = Form.useWatch('doorStyle', form);
  const productLine = Form.useWatch('productLine', form);

  // Get form values directly for disable checks (useWatch may be undefined initially)
  // Use getFieldsValue(true) to get all fields including those not yet registered
  const formValues = form.getFieldsValue(true);

  // Check if drawer style should be disabled based on drawer color or product line
  const isDrawerStyleDisabled = (styleValue: DrawerStyle) => {
    const currentDrawerColor = drawerColor ?? formValues.drawerColor;
    const currentProductLine = productLine ?? formValues.productLine;

    // Disable shaker when product line is alto
    if (currentProductLine === 'alto' && styleValue === 'shaker') {
      return true;
    }

    if (currentDrawerColor === 'grey' || currentDrawerColor === 'oak') {
      return styleValue !== 'slab';
    }
    return false;
  };

  // Check if drawer color should be disabled based on drawer style
  const isDrawerColorDisabled = (colorValue: ClosetColor) => {
    const currentDrawerStyle = drawerStyle ?? formValues.drawerStyle;
    if (currentDrawerStyle === 'shaker') {
      return colorValue !== 'white';
    }
    return false;
  };

  // Check if door style should be disabled based on door color or product line
  const isDoorStyleDisabled = (styleValue: DoorStyle) => {
    const currentDoorColor = doorColor ?? formValues.doorColor;
    const currentProductLine = productLine ?? formValues.productLine;

    // Disable shaker when product line is alto
    if (currentProductLine === 'alto' && styleValue === 'shaker') {
      return true;
    }

    if (currentDoorColor === 'grey' || currentDoorColor === 'oak') {
      return styleValue !== 'slab';
    }
    return false;
  };

  // Check if door color should be disabled based on door style
  const isDoorColorDisabled = (colorValue: ClosetColor) => {
    const currentDoorStyle = doorStyle ?? formValues.doorStyle;
    if (currentDoorStyle === 'shaker') {
      return colorValue !== 'white';
    }
    return false;
  };

  const getConditionalOptions = (name: string) => {
    if (productLine === 'garage') {
      if (name === 'color') {
        return CLOSET_COLORS.filter((color) => color.value === 'silverGrey');
      }
      if (name === 'drawerColor' || name === 'doorColor') {
        return DRAWER_DOOR_COLORS;
      }
    }
    else {
      return CLOSET_COLORS.filter((color) => color.value !== 'silverGrey' && color.value !== 'diamondGrey');
    }
    return CLOSET_COLORS;
  };

  const styleDropdowns: DropdownConfig[] = [
    {
      name: 'productLine',
      label: 'System',
      options: PRODUCT_LINES,
      required: true,
      placeholder: 'Please select',
    },
    {
      name: 'color',
      label: 'Color',
      options: getConditionalOptions('color'),
      required: true,
      placeholder: 'Select closet color',
    },
    {
      name: 'rodColor',
      label: 'Color',
      options: HARDWARE_COLORS,
      required: true,
      placeholder: 'Select rod color',
    },
    {
      name: 'handleColor',
      label: 'Color',
      options: HARDWARE_COLORS,
      required: true,
      placeholder: 'Select handle color',
    },
    {
      name: 'handleStyle',
      label: 'Style',
      options: HANDLE_STYLES,
      required: true,
      placeholder: 'Select handle style',
    },
    {
      name: 'drawerColor',
      label: 'Color',
      options: getConditionalOptions('drawerColor'),
      isDisabled: (value: any) => isDrawerColorDisabled(value as ClosetColor),
      required: true,
      placeholder: 'Select drawer color',
    },
    {
      name: 'drawerStyle',
      label: 'Style',
      options: DRAWER_STYLES,
      isDisabled: (value: any) => isDrawerStyleDisabled(value as DrawerStyle),
      required: true,
      placeholder: 'Select drawer style',
    },
    {
      name: 'doorColor',
      label: 'Color',
      options: getConditionalOptions('doorColor'),
      isDisabled: (value: any) => isDoorColorDisabled(value as ClosetColor),
      required: true,
      placeholder: 'Select door color',
    },
    {
      name: 'doorStyle',
      label: 'Style',
      options: DOOR_STYLES,
      isDisabled: (value: any) => isDoorStyleDisabled(value as DoorStyle),
      required: true,
      placeholder: 'Select door style',
    },
  ];

  const checkboxOptions = [
    { name: 'includeLegs', label: 'Legs', disabled: productLine === 'alto' },
    { name: 'includeShelf', label: 'Shelf', disabled: productLine === 'alto' },
    { name: 'includeTopDoors', label: 'Top Doors', disabled: false },
    { name: 'includeBottomDoors', label: 'Bottom Doors', disabled: false },
  ];

  const getStyleDropdown = (name: string): DropdownConfig => {
    const dropdown = styleDropdowns.find((d) => d.name === name);
    if (!dropdown) {
      throw new Error(`Dropdown config not found for name: ${name}`);
    }
    return dropdown;
  };

  // Apply disabled rules on initial load
  React.useEffect(() => {
    // Check initial drawer values
    if (drawerColor && drawerStyle && isDrawerStyleDisabled(drawerStyle)) {
      form.setFieldValue('drawerStyle', undefined);
    }
    if (drawerStyle && drawerColor && isDrawerColorDisabled(drawerColor)) {
      form.setFieldValue('drawerColor', undefined);
    }

    // Check initial door values
    if (doorColor && doorStyle && isDoorStyleDisabled(doorStyle)) {
      form.setFieldValue('doorStyle', undefined);
    }
    if (doorStyle && doorColor && isDoorColorDisabled(doorColor)) {
      form.setFieldValue('doorColor', undefined);
    }

    // Sync colors if needed
    if (!showColorFields && mainColor) {
      form.setFieldValue('doorColor', mainColor);
      form.setFieldValue('drawerColor', mainColor);
    }

    // Set default values for garage product line
    if (productLine === 'garage') {
      form.setFieldValue('color', 'silverGrey');
      form.setFieldValue('drawerColor', 'silverGrey');
      form.setFieldValue('doorColor', 'silverGrey');
    }
  }, []); // Run only on mount

  // Clear drawer style if it becomes invalid due to color change
  React.useEffect(() => {
    if (drawerColor && drawerStyle && isDrawerStyleDisabled(drawerStyle)) {
      form.setFieldValue('drawerStyle', undefined);
    }
  }, [drawerColor, drawerStyle, form]);

  // Clear drawer color if it becomes invalid due to style change
  React.useEffect(() => {
    if (drawerStyle && drawerColor && isDrawerColorDisabled(drawerColor)) {
      form.setFieldValue('drawerColor', undefined);
    }
  }, [drawerStyle, drawerColor, form]);

  // Clear door style if it becomes invalid due to color change
  React.useEffect(() => {
    if (doorColor && doorStyle && isDoorStyleDisabled(doorStyle)) {
      form.setFieldValue('doorStyle', undefined);
    }
  }, [doorColor, doorStyle, form]);

  // Clear door color if it becomes invalid due to style change
  React.useEffect(() => {
    if (doorStyle && doorColor && isDoorColorDisabled(doorColor)) {
      form.setFieldValue('doorColor', undefined);
    }
  }, [doorStyle, doorColor, form]);

  // Sync door and drawer colors with main color when not accountId 1000
  React.useEffect(() => {
    if (!showColorFields && mainColor) {
      form.setFieldValue('doorColor', mainColor);
      form.setFieldValue('drawerColor', mainColor);
    }
  }, [mainColor, showColorFields, form]);

  // Handle alto product line restrictions
  React.useEffect(() => {
    if (productLine === 'alto') {
      // Disable and clear legs and shelf
      form.setFieldValue('includeLegs', false);
      form.setFieldValue('includeShelf', false);

      // Clear shaker style if selected for doors or drawers
      if (doorStyle === 'shaker') {
        form.setFieldValue('doorStyle', undefined);
      }
      if (drawerStyle === 'shaker') {
        form.setFieldValue('drawerStyle', undefined);
      }
    }
  }, [productLine, doorStyle, drawerStyle, form]);

  return (
    <>
      <h3>Choose closet style</h3>
      <div>
        <h6>Closet</h6>
        <div className='style-row-options'>
          <div className='style-option half mr-5' key='productLine'>
            <Fields.Dropdown config={getStyleDropdown('productLine')} />
          </div>
          <div className='style-option half ml-5' key='color'>
            <Fields.Dropdown config={getStyleDropdown('color')} />
          </div>
        </div>
      </div>
      <div className='style-row'>
        <div className='style-row-inner half'>
          <h6>Door</h6>
          {showColorFields ? (
            <div className='style-row-options'>
              <div className='style-option half' key='doorColor'>
                <Fields.Dropdown config={getStyleDropdown('doorColor')} />
              </div>
              <div className='style-option half' key='doorStyle'>
                <Fields.Dropdown config={getStyleDropdown('doorStyle')} />
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'none' }} key='doorColor'>
                <Fields.Dropdown config={getStyleDropdown('doorColor')} />
              </div>
              <div className='style-option full' key='doorStyle'>
                <Fields.Dropdown config={getStyleDropdown('doorStyle')} />
              </div>
            </>
          )}
        </div>
        <div className='style-row-inner half'>
          <h6>Handle</h6>
          <div className='style-row-options'>
            <div className='style-option half' key='handleColor'>
              <Fields.Dropdown config={getStyleDropdown('handleColor')} />
            </div>
            <div className='style-option half' key='handleStyle'>
              <Fields.Dropdown config={getStyleDropdown('handleStyle')} />
            </div>
          </div>
        </div>
      </div>
      <div className='style-row'>
        <div className='style-row-inner half'>
          <h6>Drawer</h6>
          {showColorFields ? (
            <div className='style-row-options'>
              <div className='style-option half' key='drawerColor'>
                <Fields.Dropdown config={getStyleDropdown('drawerColor')} />
              </div>
              <div className='style-option half' key='drawerStyle'>
                <Fields.Dropdown config={getStyleDropdown('drawerStyle')} />
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'none' }} key='drawerColor'>
                <Fields.Dropdown config={getStyleDropdown('drawerColor')} />
              </div>
              <div className='style-option full' key='drawerStyle'>
                <Fields.Dropdown config={getStyleDropdown('drawerStyle')} />
              </div>
            </>
          )}
        </div>
        <div className='style-row-inner half'>
          <h6>Rod</h6>
          <div className='style-option full' key='rodColor'>
            <Fields.Dropdown config={getStyleDropdown('rodColor')} />
          </div>
        </div>
      </div>

      {user.isMultifamily && (
        <div>
          <h6>Additional options</h6>
          <div className='style-row-options'>
            {checkboxOptions.map((checkbox) => (
              <div key={checkbox.name} className='col-md-3 d-flex align-items-center'>
                <Form.Item name={checkbox.name} valuePropName='checked' className='mb-0'>
                  <Checkbox disabled={checkbox.disabled}>{checkbox.label}</Checkbox>
                </Form.Item>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default StyleDropdowns;
